import { useEffect, useState } from "react";
import type { TeenGigRequestListItemResponse } from "../../../types/gigTypes";
import ScheduleProposalTimeline from "../../gigs/ScheduleProposalTimeline";
import InfoTooltip from "../../ui/InfoTooltip/InfoTooltip";
import Button from "../../ui/Button/Button";
import PageSectionCard from "../../ui/Page/PageSectionCard";
import {
  toLocation,
  toSchedule,
} from "./utils";

type TeenRequestedGigDetailProps = {
  gig: TeenGigRequestListItemResponse;
  initialSection?: "details" | "schedule" | null;
  availableSections?: Array<"details" | "schedule">;
  onOpenPayoutSetup?: (gig: TeenGigRequestListItemResponse) => void;
};

const TeenRequestedGigDetail = ({
  gig,
  initialSection = null,
  availableSections = ["details", "schedule"],
  onOpenPayoutSetup,
}: TeenRequestedGigDetailProps) => {
  const defaultSection = availableSections.includes("schedule")
    && gig.status === "POSTER_PENDING"
    && gig.latestProposalByRole === "POSTER"
      ? "schedule"
      : "details";
  const resolvedInitialSection = initialSection && availableSections.includes(initialSection)
    ? initialSection
    : defaultSection;
  const [activeSection, setActiveSection] = useState<"details" | "schedule">(resolvedInitialSection);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveSection(resolvedInitialSection);
  }, [defaultSection, gig.requestId, resolvedInitialSection]);

  return (
    <div className="rounded-[1.5rem] border border-myteengig-border bg-myteengig-background/70 p-4">
      <div className="grid gap-4">
        {availableSections.length > 1 ? (
          <PageSectionCard className="p-5">
            <div className="flex justify-center">
              <div className="flex flex-wrap justify-center gap-3">
                {availableSections.includes("details") ? (
                  <Button variant={activeSection === "details" ? "primary" : "outline"} size="sm" onClick={() => setActiveSection("details")}>
                    Gig Details
                  </Button>
                ) : null}
                {availableSections.includes("schedule") ? (
                  <Button variant={activeSection === "schedule" ? "primary" : "outline"} size="sm" onClick={() => setActiveSection("schedule")}>
                    Schedule Negotiation
                  </Button>
                ) : null}
              </div>
            </div>
          </PageSectionCard>
        ) : null}

        {activeSection === "details" ? (
          <PageSectionCard className="p-5">
            <div className="grid gap-5">
              {gig.requiresScheduleReproposal ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                  <p className="font-semibold">Your requested time needs a new start time.</p>
                  <p className="mt-2 text-amber-800">
                    {gig.latestGigChangeSummary?.trim()
                      || "The poster updated this gig and your requested time no longer fits the current gig window."}
                  </p>
                </div>
              ) : null}
              {gig.requiresGigChangeAcknowledgment ? (
                <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-900">
                  <p className="font-semibold">This gig changed after you applied.</p>
                  <p className="mt-2 text-sky-800">
                    {gig.latestGigChangeSummary?.trim()
                      || "Review the updated gig details in the message and schedule thread to keep your request active."}
                  </p>
                </div>
              ) : null}
              {gig.status === "PAYOUT_SETUP_PENDING" ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900">
                  <p className="font-semibold text-emerald-700">The Poster Has Selected you!</p>
                  <p className="mt-2 text-emerald-800">
                    Finish payout setup so this gig can be funded and fully confirmed.
                  </p>
                  <Button
                    type="button"
                    className="mt-4"
                    onClick={() => onOpenPayoutSetup?.(gig)}
                  >
                    Finish Setup to Proceed
                  </Button>
                </div>
              ) : null}
              {gig.status === "FUNDING_PENDING" ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900">
                  <p className="font-semibold">Your payout setup is ready.</p>
                  <p className="mt-2 text-emerald-800">
                    The poster has been notified and still needs to pay to fully confirm the gig. It will move to scheduled gigs after checkout succeeds.
                  </p>
                </div>
              ) : null}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Summary</p>
                <div className="mt-4 grid gap-4 xl:grid-cols-2">
                  <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                    <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Location</p>
                    <p className="mt-2 text-sm font-medium leading-6 text-myteengig-text-primary">{toLocation(gig)}</p>
                  </div>

                  <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                    <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Schedule</p>
                    <p className="mt-2 text-sm font-medium leading-6 text-myteengig-text-primary">{toSchedule(gig)}</p>
                  </div>

	                  <div className="rounded-2xl bg-myteengig-background px-4 py-4">
	                    <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Gig Description</p>
	                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
	                      {gig.gigDescription?.trim() ? gig.gigDescription : "No gig description was provided."}
	                    </p>
	                  </div>

	                  {gig.gigRequiresEquipment ? (
	                    <div className="rounded-2xl bg-myteengig-background px-4 py-4">
	                      <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Equipment or supplies</p>
	                      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
	                        {gig.gigEquipmentDescription?.trim() || "Equipment is required, but details were not provided."}
	                      </p>
	                    </div>
	                  ) : null}

	                  <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                    <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Your Note</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
                      {gig.message?.trim() ? gig.message : "No note was added for this gig."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </PageSectionCard>
        ) : (
          <PageSectionCard className="p-5">
            <div className="grid gap-5">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Schedule Negotiation</p>
                  <InfoTooltip
                    placement="top-left"
                    message="This section shows every time suggestion in the order it was submitted. Review the latest update here, then either confirm the poster's time or send back a different one."
                  />
                </div>
                {gig.teenConfirmedPosterTimeAt ? (
                  <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                    <p className="text-sm font-semibold text-emerald-900">You confirmed the poster&apos;s suggested time.</p>
                    <p className="mt-2 text-sm text-emerald-800">
                      You are now waiting on the poster to accept you for the gig. If your availability changed, you can unconfirm and send a different time.
                    </p>
                  </div>
                ) : null}
                <div className="mt-3">
                  <ScheduleProposalTimeline
                    proposals={gig.scheduleProposals}
                    perspective="teen"
                    timeZone={gig.gigTimeZone}
                    emptyMessage="No timing proposals have been recorded yet."
                  />
                </div>
              </div>
            </div>
          </PageSectionCard>
        )}
      </div>
    </div>
  );
};

export default TeenRequestedGigDetail;
